import { memo } from 'react';
import { LayoutDashboard, List, FileText, Clock, Upload, CheckCircle2, History, Wand2, ArrowRight, Package, Activity, ChevronRight, Users } from 'lucide-react';

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
}

export interface RecentActivity {
  id: string;
  message: string;
  timestamp: Date;
  checklistId?: string;
}

interface NavigationSidebarProps {
  currentPage: string;
  selectedChecklistId: string | null;
  onNavigate: (page: string, checklistId?: string) => void;
  recentActivities?: RecentActivity[];
  isCollapsed?: boolean;
  userRole?: string;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'dashboard',      label: 'Dashboard',      icon: LayoutDashboard, description: 'Overview & submissions' },
  { id: 'all_checklists', label: 'All Checklists',  icon: List,            description: 'Browse all submissions' },
  { id: 'documents',      label: 'Documents',       icon: FileText,        description: 'Uploaded files' },
  { id: 'global_audit',   label: 'Audit Trail',     icon: History,         description: 'Full activity history' },
];

const CHECKLIST_SUBPAGES = ['checklist', 'upload', 'validation', 'audit', 'wizard'];

function getActivityMeta(message: string): { Icon: React.ComponentType<{ className?: string }>; color: string } {
  const m = message.toLowerCase();
  if (/auto.?map/i.test(m))             return { Icon: Wand2,       color: 'text-violet-500 bg-violet-50' };
  if (/zip/i.test(m))                   return { Icon: Package,     color: 'text-amber-500 bg-amber-50' };
  if (/upload/i.test(m))                return { Icon: Upload,      color: 'text-blue-500 bg-blue-50' };
  if (/excel/i.test(m))                 return { Icon: FileText,    color: 'text-green-600 bg-green-50' };
  if (/pdf/i.test(m))                   return { Icon: FileText,    color: 'text-red-500 bg-red-50' };
  if (/validation/i.test(m))            return { Icon: CheckCircle2, color: 'text-emerald-500 bg-emerald-50' };
  if (/audit/i.test(m))                 return { Icon: History,     color: 'text-orange-500 bg-orange-50' };
  return                                       { Icon: Activity,    color: 'text-slate-500 bg-slate-100' };
}

function getRelativeTime(date: Date): string {
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export const NavigationSidebar = memo(function NavigationSidebar({ currentPage, onNavigate, recentActivities = [], isCollapsed = false, userRole }: NavigationSidebarProps) {
  const activePage = CHECKLIST_SUBPAGES.includes(currentPage) ? 'all_checklists' : currentPage;

  return (
    <aside
      className="bg-card border-r border-border flex-shrink-0 flex flex-col overflow-hidden"
      style={{
        width: isCollapsed ? 0 : '14rem',
        transition: 'width 300ms ease-in-out',
      }}
    >

      {/* Navigation section */}
      <div className="p-3 pt-5">
        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.1em] px-3 mb-2">
          Navigation
        </p>
        <nav className="space-y-0.5">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activePage === item.id;
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all group relative ${
                  isActive
                    ? 'text-primary font-semibold'
                    : 'text-foreground hover:bg-accent'
                }`}
                style={isActive ? { background: 'rgba(0,123,255,0.08)' } : {}}
              >
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r-full" />
                )}
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                  isActive ? 'bg-primary/10' : 'bg-[#F1F5F9] group-hover:bg-muted'
                }`}>
                  <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
                <div className="min-w-0">
                  <p className={`text-sm leading-tight ${isActive ? 'text-primary' : 'text-foreground'}`}>
                    {item.label}
                  </p>
                  <p className="text-[10px] text-muted-foreground leading-tight truncate mt-0.5">
                    {item.description}
                  </p>
                </div>
              </button>
            );
          })}
          {userRole === 'admin' && (() => {
            const isActive = activePage === 'admin_users';
            return (
              <button
                key="admin_users"
                onClick={() => onNavigate('admin_users')}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all group relative ${
                  isActive ? 'text-primary font-semibold' : 'text-foreground hover:bg-accent'
                }`}
                style={isActive ? { background: 'rgba(0,123,255,0.08)' } : {}}
              >
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary rounded-r-full" />
                )}
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                  isActive ? 'bg-primary/10' : 'bg-[#F1F5F9] group-hover:bg-muted'
                }`}>
                  <Users className={`w-3.5 h-3.5 ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                </div>
                <div className="min-w-0">
                  <p className={`text-sm leading-tight ${isActive ? 'text-primary' : 'text-foreground'}`}>User Management</p>
                  <p className="text-[10px] text-muted-foreground leading-tight truncate mt-0.5">Manage team access</p>
                </div>
              </button>
            );
          })()}
        </nav>
      </div>

      {/* Recent Activity section */}
      <div className="border-t border-border/60 p-3 flex-1 min-h-0 flex flex-col">
        <div className="flex items-center justify-between px-1 mb-2">
          <div className="flex items-center gap-1.5">
            <Clock className="w-3 h-3 text-muted-foreground" />
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.1em]">
              Recent Activity
            </p>
          </div>
          <button
            onClick={() => onNavigate('global_audit')}
            className="flex items-center gap-0.5 text-[10px] text-primary font-semibold hover:underline leading-none"
          >
            View all
            <ChevronRight className="w-2.5 h-2.5" />
          </button>
        </div>

        {recentActivities.length === 0 ? (
          <div className="px-1 py-4 text-center">
            <p className="text-[10px] text-muted-foreground/50 italic">No activity yet</p>
          </div>
        ) : (
          <div className="space-y-0.5 overflow-y-auto flex-1">
            {recentActivities.map((activity) => {
              const { Icon, color } = getActivityMeta(activity.message);
              return (
                <button
                  key={activity.id}
                  onClick={() => activity.checklistId && onNavigate('checklist', activity.checklistId)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors group ${
                    activity.checklistId ? 'hover:bg-accent cursor-pointer' : 'cursor-default'
                  }`}
                >
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${color}`}>
                    <Icon className="w-2.5 h-2.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-foreground leading-tight line-clamp-2">
                      {activity.message}
                    </p>
                    <p className="text-[9px] text-muted-foreground leading-tight mt-0.5">
                      {getRelativeTime(activity.timestamp)}
                    </p>
                  </div>
                  {activity.checklistId && (
                    <ArrowRight className="w-2.5 h-2.5 text-muted-foreground/40 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom badge */}
      <div className="p-3 border-t border-border">
        <div className="rounded-xl p-3 text-center" style={{ background: 'var(--gradient-sidebar-badge)' }}>
          <p className="text-xs font-semibold text-primary">AI RegSubmission Agent</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">21 CFR Part 314 compliant</p>
        </div>
      </div>
    </aside>
  );
});
